import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import connect from 'components/lib/connect';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import LiveboardsLocation from 'models/location/liveboards';
import LiveboardType from 'models/liveboards/liveboard_type';
import NavigateBack from 'actions/current_location/navigate_back';
import NavigateToUrl from 'actions/current_location/navigate_to_url';
import { useExecuteAction } from 'components/hooks/connect_hooks';

const TABS = [LiveboardType.SUMMARY, LiveboardType.AGENTS, LiveboardType.TOPICS, LiveboardType.PEOPLE_MATCH];

const TAB_LABELS = {
  [LiveboardType.SUMMARY]: 'Summary',
  [LiveboardType.AGENTS]: 'Agents',
  [LiveboardType.TOPICS]: 'Topics',
  [LiveboardType.PEOPLE_MATCH]: 'People Match',
};

// Phone top-bar header for the liveboards page (rendered by top_bar.jsx,
// replacing the generic back-bar + agent controls — same treatment as the
// customer page). Shows "Liveboards" on the left and a tab dropdown on the
// right that navigates between the liveboard tab URLs.
export function MobileLiveboardHeader({ location }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const handleBack = useCallback(() => executeAction(NavigateBack), [executeAction]);

  const selectTab = useCallback(
    type => {
      const url = getLocationUrl(
        LiveboardsLocation.create({
          type,
          routingGroupIds: location.routingGroupIds,
          teamIds: location.teamIds,
          timezone: location.timezone,
        })
      );
      executeAction(NavigateToUrl, url);
      close();
    },
    [executeAction, location, close]
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(evt) {
      if (menuRef.current && !menuRef.current.contains(evt.target)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, close]);

  const activeType = location.type;

  return (
    <HeaderContainer data-aid="mobileLiveboardHeader">
      <BackButton aria-label="Go back" data-aid="mobileBackButton" onClick={handleBack} type="button">
        <BackArrow>&#8592;</BackArrow>
      </BackButton>
      <Title data-aid="mobileLiveboardTitle">{t('Liveboards')}</Title>
      <MenuWrapper ref={menuRef}>
        <MenuButton
          aria-expanded={isOpen}
          aria-haspopup="menu"
          data-aid="mobileLiveboardMenuButton"
          onClick={toggle}
          type="button"
        >
          <ActiveLabel>{t(TAB_LABELS[activeType])}</ActiveLabel>
          <Chevron $isOpen={isOpen}>&#9662;</Chevron>
        </MenuButton>
        {isOpen && (
          <Dropdown data-aid="mobileLiveboardMenu" role="menu">
            {TABS.map(type => (
              <DropdownItem
                $isActive={type === activeType}
                aria-checked={type === activeType}
                data-aid={`mobileLiveboardMenu-${type}`}
                key={type}
                onClick={() => selectTab(type)}
                role="menuitemradio"
                type="button"
              >
                {t(TAB_LABELS[type])}
                {type === activeType && <Check>&#10003;</Check>}
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </MenuWrapper>
    </HeaderContainer>
  );
}

MobileLiveboardHeader.propTypes = {
  location: PropTypes.instanceOf(LiveboardsLocation).isRequired,
};

function mapStateToProps({ getProvider }) {
  return {
    location: getProvider('currentLocation').get(),
  };
}

export default connect(mapStateToProps)(MobileLiveboardHeader);

const HeaderContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: ${p => p.theme.spacing.medium};
  min-width: 0;
  padding-right: 12px;
`;

const BackButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  padding: 0;
  width: 40px;

  &:active {
    opacity: 0.6;
  }
`;

const BackArrow = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: 22px;
`;

const Title = styled.div`
  color: ${p => p.theme.colors.gray900};
  flex: 1;
  font-size: var(--font-size-medium);
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
`;

const MenuButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: ${p => p.theme.borderRadius.default};
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: flex;
  font-size: ${p => p.theme.fontSize.base};
  gap: ${p => p.theme.spacing.small};
  height: 32px;
  padding: 0 ${p => p.theme.spacing.medium};

  &:active {
    background-color: ${p => p.theme.colors.gray100};
  }
`;

const ActiveLabel = styled.span`
  white-space: nowrap;
`;

const Chevron = styled.span`
  font-size: ${p => p.theme.fontSize.small};
  transform: ${p => (p.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.15s ease;
`;

const Dropdown = styled.div`
  background: ${p => p.theme.colors.white};
  border-radius: ${p => p.theme.borderRadius.large};
  box-shadow: ${p => p.theme.boxShadow.large};
  min-width: 160px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 20;
`;

const DropdownItem = styled.button`
  align-items: center;
  background-color: ${p => (p.$isActive ? p.theme.colors.green100 : 'transparent')};
  border: none;
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: var(--font-size-base);
  justify-content: space-between;
  padding: ${p => p.theme.spacing.medium} 14px;
  text-align: left;
  width: 100%;

  &:active {
    background-color: ${p => p.theme.colors.green100};
  }
`;

const Check = styled.span`
  color: ${p => p.theme.colors.green500};
  font-size: var(--font-size-base);
`;
